import React, { useState, useRef } from 'react';
import { makeStyles } from '@mui/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import Tooltip from '@mui/material/Tooltip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import WorkIcon from '@mui/icons-material/Work';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import DevicesIcon from '@mui/icons-material/Devices';
import LanguageIcon from '@mui/icons-material/Language';
import ComputerIcon from '@mui/icons-material/Computer';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BusinessIcon from '@mui/icons-material/Business';
import FilterListIcon from '@mui/icons-material/FilterList';
import useMediaQuery from '@mui/material/useMediaQuery';
import axios from '../utils/axiosConfig';

const useStyles = makeStyles((theme) => ({
    content: {
        flexGrow: 1,
        padding: '28px 32px',
        width: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
        '@media (max-width: 960px)': {
            padding: '20px 16px',
        },
        '@media (max-width: 600px)': {
            padding: '12px 10px',
        },
    },
    toolbar: {
        display: "flex",
        alignItems: "center",
        padding: theme?.spacing?.(0, 1) || '0 8px',
        ...(theme?.mixins?.toolbar || { minHeight: 56 }),
    },

    /* Page Header */
    pageHeader: {
        marginBottom: '28px',
        '@media (max-width: 600px)': {
            marginBottom: '20px',
        },
    },
    pageTitle: {
        fontSize: '1.5rem',
        fontWeight: 700,
        color: '#1a1a2e',
        letterSpacing: '-0.02em',
        '@media (max-width: 600px)': {
            fontSize: '1.2rem',
        },
    },
    pageSubtitle: {
        fontSize: '0.875rem',
        color: '#8a8a9a',
        marginTop: '4px',
    },

    /* Search Section */
    searchCard: {
        borderRadius: '16px !important',
        border: '1px solid #ede8f2',
        boxShadow: '0 1px 8px rgba(111, 41, 129, 0.06) !important',
        marginBottom: '24px',
        overflow: 'visible',
        background: 'linear-gradient(135deg, #ffffff 0%, #faf8fc 100%)',
    },
    searchCardContent: {
        padding: '24px !important',
        '@media (max-width: 600px)': {
            padding: '16px !important',
        },
    },
    searchRow: {
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        '@media (max-width: 600px)': {
            flexDirection: 'column',
            alignItems: 'stretch',
        },
    },
    searchField: {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: '#ffffff',
            fontSize: '0.95rem',
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#6f2981',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#6f2981',
                borderWidth: '2px',
            },
        },
    },
    searchButton: {
        borderRadius: '12px !important',
        padding: '8px 28px !important',
        fontWeight: '600 !important',
        fontSize: '0.9rem !important',
        textTransform: 'none !important',
        background: 'linear-gradient(135deg, #6f2981 0%, #8b3a9e 100%) !important',
        boxShadow: '0 4px 14px rgba(111, 41, 129, 0.25) !important',
        '&:hover': {
            background: 'linear-gradient(135deg, #5c2170 0%, #7a2d8b 100%) !important',
            boxShadow: '0 6px 20px rgba(111, 41, 129, 0.35) !important',
        },
        '&.Mui-disabled': {
            background: '#d4c4d9 !important',
            boxShadow: 'none !important',
        },
        '@media (max-width: 600px)': {
            padding: '10px 20px !important',
        },
    },

    /* Summary Card */
    summaryCard: {
        borderRadius: '16px !important',
        border: '1px solid #ede8f2',
        boxShadow: '0 1px 8px rgba(111, 41, 129, 0.06) !important',
        marginBottom: '24px',
    },
    summaryCardContent: {
        padding: '20px 24px !important',
        '@media (max-width: 600px)': {
            padding: '16px !important',
        },
    },
    summaryGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: '16px',
        '@media (max-width: 600px)': {
            gridTemplateColumns: '1fr',
            gap: '10px',
        },
    },
    summaryItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '14px 16px',
        borderRadius: '12px',
        backgroundColor: '#f9f7fb',
        border: '1px solid #f0ecf4',
        transition: 'background-color 0.15s',
        '&:hover': {
            backgroundColor: '#f3eff7',
        },
        '@media (max-width: 600px)': {
            padding: '10px 12px',
            gap: '8px',
        },
    },
    summaryItemIcon: {
        width: '36px',
        height: '36px',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        '@media (max-width: 600px)': {
            width: '30px',
            height: '30px',
            borderRadius: '8px',
        },
    },
    summaryItemContent: {
        flex: 1,
        minWidth: 0,
    },
    summaryLabel: {
        fontSize: '0.68rem',
        color: '#8a8a9a',
        textTransform: 'uppercase',
        fontWeight: 700,
        letterSpacing: '0.04em',
        lineHeight: 1.3,
    },
    summaryValue: {
        fontWeight: 600,
        fontSize: '0.88rem',
        color: '#1a1a2e',
        lineHeight: 1.4,
        wordBreak: 'break-word',
        '@media (max-width: 600px)': {
            fontSize: '0.8rem',
        },
    },

    /* Filter Bar */
    filterBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        marginBottom: '20px',
        flexWrap: 'wrap',
        '@media (max-width: 600px)': {
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: '12px',
            marginBottom: '16px',
        },
    },
    filterLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        '@media (max-width: 600px)': {
            flexWrap: 'wrap',
            gap: '8px',
        },
    },
    filterTitle: {
        fontWeight: 700,
        fontSize: '1.05rem',
        color: '#1a1a2e',
        whiteSpace: 'nowrap',
        '@media (max-width: 600px)': {
            fontSize: '0.95rem',
        },
    },
    filterField: {
        '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            fontSize: '0.85rem',
            backgroundColor: '#fff',
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#6f2981',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#6f2981',
            },
        },
    },

    /* Position Card */
    posCard: {
        marginBottom: '12px',
        borderRadius: '14px !important',
        overflow: 'hidden',
        border: '1px solid #ede8f2',
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.04) !important',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        '&:hover': {
            borderColor: '#d4c0e0',
            boxShadow: '0 2px 12px rgba(111, 41, 129, 0.08) !important',
        },
    },
    posCardActive: {
        borderColor: '#6f2981 !important',
        boxShadow: '0 2px 16px rgba(111, 41, 129, 0.12) !important',
    },
    posHeader: {
        display: 'flex',
        alignItems: 'center',
        padding: '16px 20px',
        cursor: 'pointer',
        userSelect: 'none',
        transition: 'background-color 0.15s',
        '&:hover': {
            backgroundColor: '#fdfaff',
        },
        '@media (max-width: 600px)': {
            padding: '12px 14px',
        },
    },
    posHeaderActive: {
        backgroundColor: '#faf5fd',
    },
    posIconWrap: {
        width: '40px',
        height: '40px',
        borderRadius: '12px',
        background: 'linear-gradient(135deg, #f3e8ff 0%, #ede0f7 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: '14px',
        flexShrink: 0,
        '@media (max-width: 600px)': {
            width: '34px',
            height: '34px',
            borderRadius: '10px',
            marginRight: '10px',
        },
    },
    posIcon: {
        color: '#6f2981',
        fontSize: '1.2rem !important',
        '@media (max-width: 600px)': {
            fontSize: '1rem !important',
        },
    },
    posInfo: {
        flex: 1,
        minWidth: 0,
    },
    posName: {
        fontWeight: 600,
        fontSize: '0.95rem',
        color: '#1a1a2e',
        lineHeight: 1.3,
        '@media (max-width: 600px)': {
            fontSize: '0.85rem',
        },
    },
    posMeta: {
        display: 'flex',
        gap: '14px',
        alignItems: 'center',
        marginTop: '3px',
        '@media (max-width: 600px)': {
            gap: '8px',
        },
    },
    posMetaItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '0.75rem',
        color: '#8a8a9a',
    },
    posActions: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginLeft: '12px',
    },
    posCountChip: {
        fontWeight: '700 !important',
        minWidth: '32px !important',
        height: '26px !important',
        fontSize: '0.8rem !important',
    },

    /* Table */
    tableWrap: {
        overflowX: 'auto',
        '-webkit-overflow-scrolling': 'touch',
    },
    tableHead: {
        '& th': {
            fontWeight: 700,
            backgroundColor: '#f9f7fb',
            color: '#6b6b7b',
            fontSize: '0.7rem',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            borderBottom: '2px solid #ede8f2',
            padding: '10px 10px',
            whiteSpace: 'normal',
            lineHeight: 1.3,
        },
    },
    tableCell: {
        fontSize: '0.82rem',
        color: '#333',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        padding: '8px 10px !important',
        lineHeight: 1.4,
    },
    examNameCell: {
        cursor: 'default',
        borderBottom: '1px dotted #c0b8cc',
        fontWeight: 500,
    },

    /* Mobile card rows */
    mobileCard: {
        padding: '14px 16px',
        borderBottom: '1px solid #f0ecf4',
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    mobileCardRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '4px 0',
    },
    mobileCardLabel: {
        fontSize: '0.68rem',
        color: '#8a8a9a',
        textTransform: 'uppercase',
        fontWeight: 700,
        letterSpacing: '0.03em',
    },
    mobileCardValue: {
        fontSize: '0.84rem',
        color: '#333',
        textAlign: 'right',
    },
    mobileExamName: {
        fontWeight: 600,
        fontSize: '0.9rem',
        color: '#1a1a2e',
        marginBottom: '8px',
        lineHeight: 1.4,
    },

    /* Empty State */
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 24px',
        textAlign: 'center',
        '@media (max-width: 600px)': {
            padding: '40px 16px',
        },
    },
    emptyStateIcon: {
        width: '72px',
        height: '72px',
        borderRadius: '20px',
        background: 'linear-gradient(135deg, #f3e8ff 0%, #ede0f7 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '20px',
    },
    emptyStateTitle: {
        fontWeight: 600,
        fontSize: '1.1rem',
        color: '#1a1a2e',
        marginBottom: '6px',
    },
    emptyStateText: {
        fontSize: '0.875rem',
        color: '#8a8a9a',
        maxWidth: '360px',
    },
}));

function fmtDate(ts) {
    if (!ts) return '-';
    const d = new Date(ts);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
}

function fmtDateShort(ts) {
    if (!ts) return '-';
    const d = new Date(ts);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function statusChip(s) {
    if (!s) return <Chip label="-" size="small" sx={{ borderRadius: '8px' }} />;
    const sl = s.toLowerCase();
    const sx = { borderRadius: '8px', fontWeight: 600, fontSize: '0.72rem' };
    if (sl === 'completed' || sl === 'submitted') return <Chip label={s} size="small" color="success" variant="outlined" sx={sx} />;
    if (sl === 'inprogress' || sl === 'in_progress' || sl === 'started') return <Chip label={s} size="small" color="warning" variant="outlined" sx={sx} />;
    if (sl === 'failed' || sl === 'error') return <Chip label={s} size="small" color="error" variant="outlined" sx={sx} />;
    return <Chip label={s} size="small" variant="outlined" sx={sx} />;
}

function resultChip(s) {
    if (!s) return '-';
    const sl = s.toLowerCase();
    const sx = { borderRadius: '8px', fontWeight: 600, fontSize: '0.72rem' };
    if (sl === 'completed' || sl === 'pass') return <Chip label={s} size="small" sx={{ ...sx, backgroundColor: '#e8f5e9', color: '#2e7d32', border: 'none' }} />;
    if (sl === 'processing' || sl === 'pending') return <Chip label={s} size="small" color="warning" variant="outlined" sx={sx} />;
    if (sl === 'fail') return <Chip label={s} size="small" color="error" variant="outlined" sx={sx} />;
    return <span style={{ fontWeight: 500, color: '#333' }}>{s}</span>;
}

function PositionCard({ posName, rows, defaultOpen, classes, isMobile }) {
    const [open, setOpen] = useState(defaultOpen);
    const latestTs = rows.reduce((max, r) => {
        const t = r.start_timestamp ? new Date(r.start_timestamp).getTime() : 0;
        return t > max ? t : max;
    }, 0);

    return (
        <Card className={`${classes.posCard} ${open ? classes.posCardActive : ''}`}>
            <Box
                className={`${classes.posHeader} ${open ? classes.posHeaderActive : ''}`}
                onClick={() => setOpen(!open)}
            >
                <Box className={classes.posIconWrap}>
                    <WorkIcon className={classes.posIcon} />
                </Box>
                <Box className={classes.posInfo}>
                    <Typography className={classes.posName}>{posName}</Typography>
                    <Box className={classes.posMeta}>
                        <Typography component="span" className={classes.posMetaItem}>
                            {rows.length} test{rows.length !== 1 ? 's' : ''}
                        </Typography>
                        {latestTs > 0 && (
                            <Typography component="span" className={classes.posMetaItem}>
                                <ScheduleIcon sx={{ fontSize: '0.85rem' }} />
                                {fmtDateShort(latestTs)}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <Box className={classes.posActions}>
                    <Chip
                        label={rows.length}
                        size="small"
                        className={classes.posCountChip}
                        sx={{
                            backgroundColor: open ? '#6f2981' : '#f3e8ff',
                            color: open ? '#fff' : '#6f2981',
                            transition: 'all 0.2s',
                        }}
                    />
                    <IconButton size="small" sx={{ color: open ? '#6f2981' : '#aaa' }}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </Box>
            </Box>
            <Collapse in={open}>
                <Divider sx={{ borderColor: '#ede8f2' }} />
                {isMobile ? (
                    <Box>
                        {rows.map((r, i) => (
                            <Box key={r.userexams_id || i} className={classes.mobileCard}>
                                <Typography className={classes.mobileExamName}>
                                    {i + 1}. {r.exam_name || '-'}
                                </Typography>
                                {(r.exam_browser || r.exam_device || r.exam_os) && (
                                    <Box className={classes.mobileCardRow}>
                                        <Typography className={classes.mobileCardLabel}>Device</Typography>
                                        <Typography className={classes.mobileCardValue}>{r.exam_browser || '-'} / {r.exam_device || '-'} / {r.exam_os || '-'}</Typography>
                                    </Box>
                                )}
                                <Box className={classes.mobileCardRow}>
                                    <Typography className={classes.mobileCardLabel}>Start</Typography>
                                    <Typography className={classes.mobileCardValue}>{fmtDate(r.start_timestamp)}</Typography>
                                </Box>
                                <Box className={classes.mobileCardRow}>
                                    <Typography className={classes.mobileCardLabel}>End</Typography>
                                    <Typography className={classes.mobileCardValue}>{fmtDate(r.finish_timestamp)}</Typography>
                                </Box>
                                <Box className={classes.mobileCardRow}>
                                    <Typography className={classes.mobileCardLabel}>Test Status</Typography>
                                    <Box>{statusChip(r.status)}</Box>
                                </Box>
                                <Box className={classes.mobileCardRow}>
                                    <Typography className={classes.mobileCardLabel}>Result</Typography>
                                    <Box>{resultChip(r.result_status)}</Box>
                                </Box>
                                <Box className={classes.mobileCardRow}>
                                    <Typography className={classes.mobileCardLabel}>Qts Answered</Typography>
                                    <Box>{r.questions_answered || "-"}/{r.total_questions || "-"}</Box>
                                </Box>
                                <Box className={classes.mobileCardRow}>
                                    <Typography className={classes.mobileCardLabel}>Time Taken</Typography>
                                    <Box>{r.time_taken || "-"}/{r.total_time || "-"}</Box>
                                </Box>
                                <Box className={classes.mobileCardRow}>
                                    <Typography className={classes.mobileCardLabel}>Percentage</Typography>
                                    <Typography className={classes.mobileCardValue} sx={{ fontWeight: 600 }}>
                                        {r.percentage != null ? `${parseFloat(r.percentage).toFixed(1)}%` : '-'}
                                    </Typography>
                                </Box>
                                {r.already_in_queue && (
                                    <Box className={classes.mobileCardRow}>
                                        <Typography className={classes.mobileCardLabel}>Queue</Typography>
                                        <Chip label="Awaiting Processing" size="small" color="warning" variant="outlined" sx={{ borderRadius: '8px', fontSize: '0.7rem' }} />
                                    </Box>
                                )}
                                {r.mongodb_skipped && (
                                    <Box className={classes.mobileCardRow}>
                                        <Typography className={classes.mobileCardLabel}>MongoDB</Typography>
                                        <Chip label="DB Save Skipped" size="small" color="error" variant="outlined" sx={{ borderRadius: '8px', fontSize: '0.7rem' }} />
                                    </Box>
                                )}
                                <Box className={classes.mobileCardRow}>
                                    <Typography className={classes.mobileCardLabel}>Proctoring</Typography>
                                    <Typography className={classes.mobileCardValue}>
                                        {r.sub_scores && r.sub_scores.length > 0 ?
                                            <ul style={{ listStyle: "none" }}>
                                                {r.sub_scores.map((item, i) => (
                                                    <li key={i}>{item.label} : {item.occurrences}</li>
                                                ))}
                                            </ul>
                                            :
                                            "-"
                                        }
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0, overflowX: 'hidden' }}>
                        <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                            <colgroup>
                                <col style={{ width: '3.5%' }} />
                                <col style={{ width: '18%' }} />
                                <col style={{ width: '14%' }} />
                                <col style={{ width: '14%' }} />
                                <col style={{ width: '10%' }} />
                                <col style={{ width: '10%' }} />
                                <col style={{ width: '10%' }} />
                                <col style={{ width: '10%' }} />
                                <col style={{ width: '8%' }} />
                                <col style={{ width: '11.5%' }} />
                                <col style={{ width: '11%' }} />
                                <col style={{ width: '15%' }} />
                            </colgroup>
                            <TableHead className={classes.tableHead}>
                                <TableRow>
                                    <TableCell>#</TableCell>
                                    <TableCell>Exam Name</TableCell>
                                    <TableCell>Start Time</TableCell>
                                    <TableCell>End Time</TableCell>
                                    <TableCell>Test Status</TableCell>
                                    <TableCell>Result Status</TableCell>
                                    <TableCell>Qts Answered</TableCell>
                                    <TableCell>Time Taken</TableCell>
                                    <TableCell>%</TableCell>
                                    <TableCell>Queue</TableCell>
                                    <TableCell>MongoDB Status</TableCell>
                                    <TableCell>Proctoring</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((r, i) => (
                                    <TableRow
                                        key={r.userexams_id || i}
                                        hover
                                        sx={{
                                            '&:hover': { backgroundColor: '#fdfaff' },
                                            '&:last-child td': { borderBottom: 0 },
                                        }}
                                    >
                                        <TableCell className={classes.tableCell} sx={{ color: '#8a8a9a', fontWeight: 600 }}>{i + 1}</TableCell>
                                        <TableCell className={classes.tableCell}>
                                            {r.exam_browser || r.exam_device || r.exam_os ? (
                                                <Tooltip arrow placement="bottom-start" title={
                                                    <Box sx={{ p: 0.5 }}>
                                                        <Typography variant="caption" display="block">Browser: <b>{r.exam_browser || '-'}</b></Typography>
                                                        <Typography variant="caption" display="block">Device: <b>{r.exam_device || '-'}</b></Typography>
                                                        <Typography variant="caption" display="block">OS: <b>{r.exam_os || '-'}</b></Typography>
                                                    </Box>
                                                }>
                                                    <span className={classes.examNameCell}>{r.exam_name || '-'}</span>
                                                </Tooltip>
                                            ) : (r.exam_name || '-')}
                                        </TableCell>
                                        <TableCell className={classes.tableCell}>{fmtDate(r.start_timestamp)}</TableCell>
                                        <TableCell className={classes.tableCell}>{fmtDate(r.finish_timestamp)}</TableCell>
                                        <TableCell className={classes.tableCell}>{statusChip(r.status)}</TableCell>
                                        <TableCell className={classes.tableCell}>{resultChip(r.result_status)}</TableCell>
                                        <TableCell className={classes.tableCell}>{r.questions_answered || "-"}/{r.total_questions || "-"}</TableCell>
                                        <TableCell className={classes.tableCell}>{r.time_taken || "-"}/<br />{r.total_time || "-"}</TableCell>
                                        <TableCell className={classes.tableCell} sx={{ fontWeight: 600 }}>
                                            {r.percentage != null ? `${parseFloat(r.percentage).toFixed(1)}%` : '-'}
                                        </TableCell>
                                        <TableCell className={classes.tableCell}>
                                            {r.already_in_queue ? <Chip label="Awaiting Processing" size="small" color="warning" variant="outlined" sx={{ borderRadius: '8px', fontSize: '0.7rem' }} /> : 'NOT PRESENT'}
                                        </TableCell>
                                        <TableCell className={classes.tableCell}>
                                            {r.mongodb_skipped ? <Chip label="DB Save Skipped" size="small" color="error" variant="outlined" sx={{ borderRadius: '8px', fontSize: '0.7rem' }} /> : 'NOT SKIPPED'}
                                        </TableCell>
                                        <TableCell className={classes.tableCell}>
                                            {r.sub_scores && r.sub_scores.length > 0 ?
                                                <ul>
                                                    {r.sub_scores.map((item, i) => (
                                                        <li key={i} style={{ fontSize: "10px" }}>{item.label} : {item.occurrences}</li>
                                                    ))}
                                                </ul>
                                                :
                                                "-"
                                            }
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Collapse>
        </Card>
    );
}

export default function CandidateSearch() {
    const classes = useStyles();
    const isMobile = useMediaQuery('(max-width:600px)');
    const isTablet = useMediaQuery('(max-width:960px)');
    const [username, setUsername] = useState('');
    const [allData, setAllData] = useState([]);
    const [deviceInfo, setDeviceInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searched, setSearched] = useState(false);
    const [selectedOrg, setSelectedOrg] = useState('');
    const [filterText, setFilterText] = useState('');
    const filterTimer = useRef(null);

    const doSearch = async () => {
        const q = username.trim();
        if (!q) return;
        setLoading(true);
        setError('');
        setSearched(true);
        setAllData([]);
        setDeviceInfo(null);
        setSelectedOrg('');
        setFilterText('');

        try {
            const res = await axios.post('/admin/searchCandidate', { username: q });
            if (!res.data.success) {
                setError(res.data.message || 'Search failed.');
                return;
            }
            if (!res.data.data || res.data.data.length === 0) {
                setError('No results found.');
                return;
            }
            const sorted = [...res.data.data].sort((a, b) => {
                const ta = a.start_timestamp ? new Date(a.start_timestamp).getTime() : 0;
                const tb = b.start_timestamp ? new Date(b.start_timestamp).getTime() : 0;
                return tb - ta;
            });
            setAllData(sorted);
            setDeviceInfo(res.data.deviceInfo || null);

            const orgs = getOrgs(sorted);
            if (orgs.length > 0) setSelectedOrg(orgs[0]);
        } catch (err) {
            setError('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const getOrgs = (data) => {
        const orgLatest = {};
        for (const row of data) {
            const org = row.org_name || 'Unknown Org';
            const ts = row.start_timestamp ? new Date(row.start_timestamp).getTime() : 0;
            if (!orgLatest[org] || ts > orgLatest[org]) orgLatest[org] = ts;
        }
        return Object.keys(orgLatest).sort((a, b) => orgLatest[b] - orgLatest[a]);
    };

    const orgs = allData.length > 0 ? getOrgs(allData) : [];
    const orgData = allData.filter(r => (r.org_name || 'Unknown Org') === selectedOrg);

    const q = filterText.toLowerCase().trim();
    const filtered = q
        ? orgData.filter(r => (r.position_name || '').toLowerCase().includes(q) || (r.exam_name || '').toLowerCase().includes(q))
        : orgData;

    const groups = {};
    const order = [];
    for (const row of filtered) {
        const key = row.position_name || 'No Position';
        if (!groups[key]) { groups[key] = []; order.push(key); }
        groups[key].push(row);
    }

    const posLatest = {};
    for (const row of filtered) {
        const pos = row.position_name || 'No Position';
        const ts = row.start_timestamp ? new Date(row.start_timestamp).getTime() : 0;
        if (!posLatest[pos] || ts > posLatest[pos]) posLatest[pos] = ts;
    }
    const sortedOrder = [...order].sort((a, b) => (posLatest[b] || 0) - (posLatest[a] || 0));

    const handleFilter = (val) => {
        clearTimeout(filterTimer.current);
        filterTimer.current = setTimeout(() => setFilterText(val), 200);
    };

    return (
        <main className={classes.content}>
            <div className={classes.toolbar} />

            {/* Page Header */}
            <Box className={classes.pageHeader}>
                <Typography className={classes.pageTitle}>Search Candidates</Typography>
                <Typography className={classes.pageSubtitle}>
                    Look up candidate test history by email or username
                </Typography>
            </Box>

            {/* Search */}
            <Card className={classes.searchCard}>
                <CardContent className={classes.searchCardContent}>
                    <Box className={classes.searchRow}>
                        <TextField
                            size="small"
                            placeholder="Enter email or username..."
                            value={username}
                            onChange={e => setUsername(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && doSearch()}
                            className={classes.searchField}
                            sx={{ flex: 1 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PersonSearchIcon sx={{ color: '#6f2981', opacity: 0.6 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Button
                            variant="contained"
                            onClick={doSearch}
                            disabled={loading}
                            className={classes.searchButton}
                            disableElevation
                        >
                            {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Search'}
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {/* Error/Info Alert */}
            {error && (
                <Alert
                    severity={searched && !loading ? 'info' : 'error'}
                    sx={{
                        mb: 2.5,
                        borderRadius: '12px',
                        '& .MuiAlert-icon': { alignItems: 'center' },
                    }}
                >
                    {error}
                </Alert>
            )}

            {/* Empty State - before first search */}
            {!searched && !loading && allData.length === 0 && (
                <Box className={classes.emptyState}>
                    <Box className={classes.emptyStateIcon}>
                        <PersonSearchIcon sx={{ fontSize: '2rem', color: '#6f2981' }} />
                    </Box>
                    <Typography className={classes.emptyStateTitle}>
                        Search for a Candidate
                    </Typography>
                    <Typography className={classes.emptyStateText}>
                        Enter a candidate's email address or username above to view their test history and results.
                    </Typography>
                </Box>
            )}

            {allData.length > 0 && (
                <>
                    {/* Summary Card */}
                    <Card className={classes.summaryCard}>
                        <CardContent className={classes.summaryCardContent}>
                            <Box className={classes.summaryGrid}>
                                {/* Org Selector */}
                                <Box className={classes.summaryItem}>
                                    <Box className={classes.summaryItemIcon} sx={{ backgroundColor: '#f3e8ff' }}>
                                        <BusinessIcon sx={{ fontSize: '1.1rem', color: '#6f2981' }} />
                                    </Box>
                                    <Box className={classes.summaryItemContent}>
                                        <Typography className={classes.summaryLabel}>Organization</Typography>
                                        {orgs.length > 1 ? (
                                            <FormControl size="small" fullWidth>
                                                <Select
                                                    value={selectedOrg}
                                                    onChange={e => { setSelectedOrg(e.target.value); setFilterText(''); }}
                                                    sx={{
                                                        fontSize: '0.85rem',
                                                        fontWeight: 600,
                                                        color: '#1a1a2e',
                                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ede8f2' },
                                                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#6f2981' },
                                                        borderRadius: '8px',
                                                    }}
                                                    variant="outlined"
                                                >
                                                    {orgs.map(org => (
                                                        <MenuItem key={org} value={org}>{org}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        ) : (
                                            <Typography className={classes.summaryValue}>{selectedOrg}</Typography>
                                        )}
                                    </Box>
                                </Box>

                                {deviceInfo && (
                                    <>
                                        <Box className={classes.summaryItem}>
                                            <Box className={classes.summaryItemIcon} sx={{ backgroundColor: '#e8f4fd' }}>
                                                <LanguageIcon sx={{ fontSize: '1.1rem', color: '#1976d2' }} />
                                            </Box>
                                            <Box className={classes.summaryItemContent}>
                                                <Typography className={classes.summaryLabel}>Browser</Typography>
                                                <Typography className={classes.summaryValue}>{deviceInfo.browser_name || '-'}</Typography>
                                            </Box>
                                        </Box>
                                        <Box className={classes.summaryItem}>
                                            <Box className={classes.summaryItemIcon} sx={{ backgroundColor: '#fff3e0' }}>
                                                <DevicesIcon sx={{ fontSize: '1.1rem', color: '#e65100' }} />
                                            </Box>
                                            <Box className={classes.summaryItemContent}>
                                                <Typography className={classes.summaryLabel}>Device</Typography>
                                                <Typography className={classes.summaryValue}>{deviceInfo.device_type || '-'}</Typography>
                                            </Box>
                                        </Box>
                                        <Box className={classes.summaryItem}>
                                            <Box className={classes.summaryItemIcon} sx={{ backgroundColor: '#e8f5e9' }}>
                                                <ComputerIcon sx={{ fontSize: '1.1rem', color: '#2e7d32' }} />
                                            </Box>
                                            <Box className={classes.summaryItemContent}>
                                                <Typography className={classes.summaryLabel}>OS</Typography>
                                                <Typography className={classes.summaryValue}>{deviceInfo.ops || '-'}</Typography>
                                            </Box>
                                        </Box>
                                        <Box className={classes.summaryItem}>
                                            <Box className={classes.summaryItemIcon} sx={{ backgroundColor: '#fce4ec' }}>
                                                <AccessTimeIcon sx={{ fontSize: '1.1rem', color: '#c62828' }} />
                                            </Box>
                                            <Box className={classes.summaryItemContent}>
                                                <Typography className={classes.summaryLabel}>Last Login</Typography>
                                                <Typography className={classes.summaryValue}>{fmtDate(deviceInfo.logged_on)}</Typography>
                                            </Box>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Filter Bar */}
                    <Box className={classes.filterBar}>
                        <Box className={classes.filterLeft}>
                            <Typography className={classes.filterTitle}>
                                Positions ({sortedOrder.length} position{sortedOrder.length !== 1 ? 's' : ''}, {filtered.length} test{filtered.length !== 1 ? 's' : ''})
                            </Typography>
                        </Box>
                        <TextField
                            size="small"
                            placeholder="Filter positions or tests..."
                            onChange={e => handleFilter(e.target.value)}
                            className={classes.filterField}
                            sx={{ width: { xs: '100%', sm: '380px' } }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <FilterListIcon sx={{ fontSize: '1.1rem', color: '#8a8a9a' }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>

                    {/* No Results for Filter */}
                    {sortedOrder.length === 0 && (
                        <Card sx={{ textAlign: 'center', py: 5, borderRadius: '14px', border: '1px solid #ede8f2', boxShadow: 'none' }}>
                            <Typography sx={{ color: '#8a8a9a', fontSize: '0.95rem' }}>
                                No matching positions or tests found.
                            </Typography>
                        </Card>
                    )}

                    {/* Position Cards */}
                    {sortedOrder.map((posName, idx) => (
                        <PositionCard
                            key={posName}
                            posName={posName}
                            rows={groups[posName]}
                            defaultOpen={idx === 0 || !!q || groups[posName].some(r => r.mongodb_skipped || r.already_in_queue || ['failed', 'error'].includes((r.status || '').toLowerCase()) || ['fail'].includes((r.result_status || '').toLowerCase()))}
                            classes={classes}
                            isMobile={isMobile}
                        />
                    ))}
                </>
            )}
        </main>
    );
}
